import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { Box, Typography, TextField, Button, Paper, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, CircularProgress, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const ManageUsersPage = () => {
  const { createNewUser, listAllUsers, deleteUserByUid } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addUserError, setAddUserError] = useState('');
  const [addUserSuccess, setAddUserSuccess] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const userList = await listAllUsers();
      setUsers(userList);
    } catch (err) {
      setError('Failed to fetch users. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [listAllUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');
    setAddUserSuccess('');
    if (!email || !password) {
      setAddUserError('Please enter both email and password.');
      return;
    }
    try {
      await createNewUser(email, password);
      setAddUserSuccess(`Successfully created user: ${email}`);
      setEmail('');
      setPassword('');
      fetchUsers(); // Refresh the user list
    } catch (err) {
      setAddUserError(err.message || 'Failed to add user.');
      console.error(err);
    }
  };

  const handleDeleteUser = async (uid) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await deleteUserByUid(uid);
        alert('User deleted successfully.');
        fetchUsers(); // Refresh the user list
      } catch (err) {
        alert('Failed to delete user.');
        console.error(err);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Manage Users</Typography>

      {/* Add User Form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Add New User</Typography>
        <form onSubmit={handleAddUser}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          {addUserError && <Alert severity="error" sx={{ mb: 2 }}>{addUserError}</Alert>}
          {addUserSuccess && <Alert severity="success" sx={{ mb: 2 }}>{addUserSuccess}</Alert>}
          <Button type="submit" variant="contained" color="primary">Add User</Button>
        </form>
      </Paper>

      {/* User List */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Existing Users</Typography>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <List>
            {users.map((user) => (
              <ListItem key={user.uid} divider>
                <ListItemText primary={user.email} secondary={`UID: ${user.uid}`} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteUser(user.uid)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default ManageUsersPage;
